import { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Offcanvas } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import type { ClientCustomerType, LeadCloudFileType } from '@econtractor/types';
import type { CloudUploadScope } from 'api/apiCloudflare';
import * as apiClient from 'api/apiClients';
import { useModal } from 'context/ModalContext';
import { getButtonsNoYes } from 'context/helpers/helperModalContext';
import { useToast } from 'context/ToastContext';
import spinnerTitle from 'components/others/spinnerTitle';
import SvgSave from 'components/svg/SvgSave';
import { parseCloudFiles } from 'modules/files';
import {
    CustomerForm,
    EMPTY_CUSTOMER,
    normalizeCustomerPayload,
    parseObjectCustomer,
    useCustomerFormFields,
} from 'modules/objects';
import ClientsSelect from '../../ClientsSelect';

interface OffClientCustomerEditProps {
    clientId: number | null;
    customer: ClientCustomerType | null;
    isCreate: boolean;
    formNonce: number;
    onHide: () => void;
    onSaved: () => Promise<void>;
}

const OffClientCustomerEdit = ({
    clientId,
    customer,
    isCreate,
    formNonce,
    onHide,
    onSaved,
}: OffClientCustomerEditProps) => {
    const { t } = useTranslation(['Clients', 'Objects', 'common']);
    const { showToast } = useToast();
    const { showDlg } = useModal();
    const show = isCreate || customer !== null;
    const [selectedClientId, setSelectedClientId] = useState<number | null>(clientId);
    const [originalClientId, setOriginalClientId] = useState<number | null>(
        isCreate ? null : Number(customer?.clientId || clientId || 0) || null,
    );
    const [savedCustomerId, setSavedCustomerId] = useState(0);
    const [customerFiles, setCustomerFiles] = useState<LeadCloudFileType[]>([]);
    const filesOwnerIdRef = useRef(0);

    const lookupClientId = originalClientId || selectedClientId;

    useEffect(() => {
        setSelectedClientId(clientId);
        setOriginalClientId(isCreate ? null : Number(customer?.clientId || clientId || 0) || null);
        setSavedCustomerId(isCreate ? 0 : Number(customer?.ID || 0));
        setCustomerFiles([]);
        filesOwnerIdRef.current = 0;
    }, [clientId, formNonce, isCreate, customer?.ID, customer?.clientId]);

    const initialCustomer = useMemo(
        () =>
            isCreate
                ? { ...EMPTY_CUSTOMER }
                : parseObjectCustomer({
                      ...customer?.customer,
                      fullName: customer?.fullName,
                      edrpouOrIpn: customer?.edrpouOrIpn,
                      vatEnabled: customer?.vatEnabled ?? customer?.customer?.vatEnabled,
                  }),
        [isCreate, customer],
    );

    const fields = useCustomerFormFields({
        initialCustomer,
        resetKey: `${isCreate ? 'create' : customer?.ID || 0}-${formNonce}`,
        edrpouReadOnly: !isCreate,
    });

    useEffect(() => {
        if (!lookupClientId || !savedCustomerId) {
            if (!savedCustomerId) setCustomerFiles([]);
            return;
        }
        if (filesOwnerIdRef.current === savedCustomerId) return;

        let cancelled = false;
        void (async () => {
            const row = await apiClient.getClientCustomer(lookupClientId, savedCustomerId);
            if (cancelled) return;
            filesOwnerIdRef.current = savedCustomerId;
            setCustomerFiles(parseCloudFiles(row?.files));
        })();

        return () => {
            cancelled = true;
        };
    }, [lookupClientId, savedCustomerId, formNonce]);

    const toastSaveError = (errCode?: string) => {
        if (errCode === 'EDRPOU_EXISTS') {
            showToast(t('subjects.messEdrpouExists'), 'warning');
            return;
        }
        if (errCode === 'CLIENT_REQUIRED') {
            showToast(t('subjects.messClientRequired'), 'warning');
            return;
        }
        if (errCode === 'EDRPOU_REQUIRED') {
            showToast(t('subjects.messCodeRequired'), 'warning');
            return;
        }
        showToast(t('common:messError'), 'danger');
    };

    const isClientChanged = () => {
        const fromId = Number(originalClientId || 0);
        const toId = Number(selectedClientId || 0);
        return Boolean(fromId && toId && fromId !== toId);
    };

    const confirmClientChangeIfNeeded = (): Promise<boolean> => {
        if (!isClientChanged()) return Promise.resolve(true);

        return new Promise((resolve) => {
            const buttons = [...getButtonsNoYes(t)];
            buttons[0].handleClick = () => {
                showDlg(false);
                resolve(false);
            };
            buttons[1].handleClick = () => {
                showDlg(false);
                resolve(true);
            };
            showDlg({
                type: 'confirm',
                buttons,
                text: t('subjects.confirmChangeClient'),
            });
        });
    };

    const persistExisting = async (customerId: number): Promise<boolean> => {
        if (!lookupClientId || !selectedClientId) {
            showToast(t('subjects.messClientRequired'), 'warning');
            return false;
        }

        const payload = normalizeCustomerPayload(fields.formValues);
        fields.setIsSaving(true);
        const result = await apiClient.updateClientCustomer(
            lookupClientId,
            customerId,
            payload,
            selectedClientId,
        );
        fields.setIsSaving(false);

        if (result.ok === false) {
            toastSaveError(result.errCode);
            return false;
        }

        setOriginalClientId(selectedClientId);
        return true;
    };

    const handleCustomerFilesChange = async (nextFiles: LeadCloudFileType[]): Promise<boolean> => {
        setCustomerFiles(nextFiles);
        return true;
    };

    const handleEnsureCustomerForFiles = async (): Promise<CloudUploadScope | false> => {
        if (!selectedClientId) {
            showToast(t('subjects.messClientRequired'), 'warning');
            return false;
        }

        const payload = normalizeCustomerPayload(fields.formValues);
        if (!payload.edrpouOrIpn) {
            showToast(t('subjects.messCodeRequired'), 'warning');
            return false;
        }

        if (savedCustomerId) {
            const confirmed = await confirmClientChangeIfNeeded();
            if (!confirmed) return false;
            const ok = await persistExisting(savedCustomerId);
            if (!ok) return false;
            filesOwnerIdRef.current = savedCustomerId;
            return { clientCustomerId: savedCustomerId };
        }

        fields.setIsSaving(true);
        const result = await apiClient.upsertClientCustomer(selectedClientId, payload);
        fields.setIsSaving(false);
        if (result.ok === false) {
            toastSaveError(result.errCode);
            return false;
        }

        const clientCustomerId = Number(result.id || 0);
        if (!clientCustomerId) {
            showToast(t('common:messError'), 'danger');
            return false;
        }

        filesOwnerIdRef.current = clientCustomerId;
        setSavedCustomerId(clientCustomerId);
        setOriginalClientId(selectedClientId);
        return { clientCustomerId };
    };

    const finishSave = async (isNew: boolean) => {
        showToast(isNew ? t('subjects.messSaved') : t('subjects.messUpdated'), 'success');
        await onSaved();
        onHide();
    };

    const handleSave = async () => {
        if (fields.isSaving || fields.isAiBusy || fields.isYcBusy) return;

        if (!selectedClientId) {
            showToast(t('subjects.messClientRequired'), 'warning');
            return;
        }

        const payload = normalizeCustomerPayload(fields.formValues);
        if (isCreate && !payload.edrpouOrIpn) {
            showToast(t('subjects.messCodeRequired'), 'warning');
            return;
        }

        const existingId = savedCustomerId || Number(customer?.ID || 0);
        if (existingId) {
            const confirmed = await confirmClientChangeIfNeeded();
            if (!confirmed) return;
            const ok = await persistExisting(existingId);
            if (ok) await finishSave(isCreate);
            return;
        }

        fields.setIsSaving(true);
        const result = await apiClient.upsertClientCustomer(selectedClientId, payload);
        fields.setIsSaving(false);
        if (result.ok === false) {
            toastSaveError(result.errCode);
            return;
        }

        setOriginalClientId(selectedClientId);
        await finishSave(true);
    };

    return (
        <Offcanvas show={show} onHide={onHide} placement="end" className="off-client-customer-edit">
            <Offcanvas.Header className="border-bottom" closeButton>
                <Offcanvas.Title>
                    {isCreate ? t('subjects.titleCreate') : t('subjects.titleUpdate')}
                </Offcanvas.Title>
            </Offcanvas.Header>
            <Offcanvas.Body className="position-relative pb-0 d-flex flex-column px-0">
                <div className="off-coming-form d-flex flex-column h-100">
                    <div className="off-coming-content">
                        <div className="px-3 pb-3">
                            <label className="form-label">{t('subjects.fields.client')}</label>
                            <ClientsSelect
                                value={selectedClientId}
                                allowNew={false}
                                isClearable={false}
                                placeholder={t('selectTitle')}
                                onChange={(option) => {
                                    setSelectedClientId(option?.value ? Number(option.value) : null);
                                }}
                            />
                        </div>
                        <CustomerForm
                            formValues={fields.formValues}
                            edrpouReadOnly={fields.edrpouReadOnly}
                            showSaveButton={false}
                            isSaving={fields.isSaving}
                            isYcBusy={fields.isYcBusy}
                            isAiBusy={fields.isAiBusy}
                            isDecliningName={fields.isDecliningName}
                            handleDeclineRepresentativeName={fields.handleDeclineRepresentativeName}
                            ycCodeReady={fields.ycCodeReady}
                            yesLabel={fields.yesLabel}
                            noLabel={fields.noLabel}
                            getPersonTypeLabel={fields.getPersonTypeLabel}
                            handleChange={fields.handleChange}
                            handleFetchYouControl={fields.handleFetchYouControl}
                            refInputFile={fields.refInputFile}
                            uploadProgress={fields.uploadProgress}
                            aiPhase={fields.aiPhase}
                            processingLabel={fields.processingLabel}
                            handleOpenClick={fields.handleOpenClick}
                            handleChangeFile={fields.handleChangeFile}
                            handleCancel={fields.handleCancel}
                            className="px-3 pb-3"
                            customerFiles={customerFiles}
                            clientCustomerId={savedCustomerId}
                            canEditFiles={true}
                            saveFirstMessage={t('subjects.messSaveCustomerFirst')}
                            onCustomerFilesChange={handleCustomerFilesChange}
                            onEnsureCustomerForFiles={handleEnsureCustomerForFiles}
                        />
                    </div>
                    <div className="p-3 offcanvas-footer off-coming-footer">
                        <div className="d-flex justify-content-end gap-2">
                            <Button
                                variant="info"
                                className="min100px"
                                disabled={
                                    !selectedClientId
                                    || fields.isSaving
                                    || fields.isAiBusy
                                    || fields.isYcBusy
                                }
                                onClick={() => void handleSave()}
                            >
                                {spinnerTitle(
                                    <>
                                        <SvgSave className="me-2 mtop-2" />
                                        {t('common:buttons.save')}
                                    </>,
                                    fields.isSaving,
                                    t('common:buttons.saving'),
                                )}
                            </Button>
                        </div>
                    </div>
                </div>
            </Offcanvas.Body>
        </Offcanvas>
    );
};

export default OffClientCustomerEdit;
